import type { ProcessedListingData } from "../../../@types/sharedSchema";

/**
 * Who a listing's matching contacts ARE. A sale listing matches buyers (קונים),
 * a rental matches renters (שוכרים).
 *
 * This is wording only — the matching itself has always handled both markets:
 * `getFilteredContactsMatchingListing` (server, listingsMatch.service.ts) picks
 * the שוכר customer type and the `customerBudgetRent` field when the listing is
 * `listingListedFor: 'rent'`. Until this existed the whole UI was hardcoded to
 * "קונים", which is why the tab was hidden for rentals rather than relabeled.
 */
export type MatchAudience = "buyers" | "renters";

const NAMESPACE = "listingMatchToContactsPage";

/** Reads whichever market field the listing shape carries. */
export function matchAudienceForListing(
  listing:
    | Pick<ProcessedListingData, "transactionType" | "listingListedFor">
    | null
    | undefined
): MatchAudience {
  const listedFor = listing?.transactionType ?? listing?.listingListedFor;
  return listedFor === "rent" ? "renters" : "buyers";
}

/**
 * Resolve a `listingMatchToContactsPage.*` key for an audience.
 *
 * Renter copy lives in a `renters.` sub-tree holding ONLY the strings that
 * actually differ, and this returns i18next's key-fallback array so anything not
 * overridden keeps the existing wording. That way adding a string to the buyer
 * copy can never leave the renter view rendering a raw key.
 */
export function audienceKey(audience: MatchAudience, key: string): string[] {
  return audience === "renters"
    ? [`${NAMESPACE}.renters.${key}`, `${NAMESPACE}.${key}`]
    : [`${NAMESPACE}.${key}`];
}
